import React, { useState, useEffect } from "react";
import { motion } from "framer-motion";
import {
  Brain, Copy, Download, Key, RefreshCw, Eye, EyeOff,
  FileText, Code, ExternalLink, Check, Loader2, Shield
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { pageTransition, fadeUp } from "@/lib/animations";
import api from "@/lib/api";
import { toast } from "sonner";

export default function KnowledgeBasePage() {
  const [kb, setKb] = useState(null);
  const [markdown, setMarkdown] = useState("");
  const [apiKey, setApiKey] = useState(null);
  const [showKey, setShowKey] = useState(false);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState("preview");
  const [copied, setCopied] = useState(false);

  useEffect(() => { loadAll(); }, []);

  const loadAll = async () => {
    setLoading(true);
    try {
      const [kbRes, mdRes, keyRes] = await Promise.all([
        api.get("/knowledge-base"),
        api.get("/knowledge-base/export?format=markdown"),
        api.get("/knowledge-base/api-key"),
      ]);
      setKb(kbRes.data);
      setMarkdown(mdRes.data.markdown);
      setApiKey(keyRes.data);
    } catch { toast.error("Failed to load knowledge base"); }
    finally { setLoading(false); }
  };

  const handleCopy = async (text) => {
    await navigator.clipboard?.writeText(text);
    setCopied(true);
    toast.success("Copied to clipboard!");
    setTimeout(() => setCopied(false), 2000);
  };

  const handleDownload = (content, ext) => {
    const blob = new Blob([typeof content === "string" ? content : JSON.stringify(content, null, 2)], { type: "text/plain" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `my-knowledge-base.${ext}`;
    a.click();
    URL.revokeObjectURL(url);
    toast.success(`Downloaded as .${ext}`);
  };

  const handleRegenKey = async () => {
    if (!window.confirm("Regenerate API key? The old key will stop working.")) return;
    try {
      const res = await api.post("/knowledge-base/api-key/regenerate");
      setApiKey(res.data);
      toast.success("API key regenerated");
    } catch { toast.error("Failed to regenerate"); }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-full p-12">
        <Loader2 className="w-8 h-8 text-[#00FF88] animate-spin" />
      </div>
    );
  }

  const s = kb?.sections || {};
  const p = s.personality || {};
  const apiUrl = window.location.origin + "/api/knowledge-base/external/" + (apiKey?.api_key || "YOUR_KEY");

  return (
    <motion.div variants={pageTransition} initial="hidden" animate="visible" className="p-6 md:p-10 max-w-[1000px] mx-auto pb-24" data-testid="knowledge-base-page">
      <div className="mb-8">
        <div className="flex items-center gap-3 mb-3">
          <div className="w-10 h-10 rounded-xl bg-[#00FF88]/10 flex items-center justify-center">
            <Brain className="w-5 h-5 text-[#00FF88]" />
          </div>
          <p className="text-xs tracking-[0.3em] uppercase text-[#00FF88] font-medium">AI Context</p>
        </div>
        <h1 className="text-3xl md:text-4xl font-bold tracking-tight" style={{ fontFamily: 'Manrope' }}>
          Knowledge Base
        </h1>
        <p className="text-sm text-slate-400 mt-1">Your personal context file for any LLM — always up to date</p>
      </div>

      {/* Quick Actions */}
      <div className="flex flex-wrap gap-3 mb-8">
        <Button
          onClick={() => handleCopy(markdown)}
          data-testid="copy-context-btn"
          className="bg-[#00FF88] text-black hover:bg-[#00CC6A] rounded-full px-6 font-bold"
        >
          {copied ? <Check className="w-4 h-4 mr-2" /> : <Copy className="w-4 h-4 mr-2" />}
          {copied ? "Copied!" : "Copy for ChatGPT / Claude"}
        </Button>
        <Button
          onClick={() => handleDownload(markdown, "md")}
          variant="outline"
          data-testid="download-md-btn"
          className="border-white/10 text-slate-400 rounded-full"
        >
          <Download className="w-4 h-4 mr-2" /> Download .md
        </Button>
        <Button
          onClick={() => handleDownload(kb, "json")}
          variant="outline"
          data-testid="download-json-btn"
          className="border-white/10 text-slate-400 rounded-full"
        >
          <Code className="w-4 h-4 mr-2" /> Download .json
        </Button>
      </div>

      {/* Tabs */}
      <div className="flex gap-1.5 mb-6 bg-white/[0.02] border border-white/[0.06] rounded-xl p-1.5 w-fit">
        {[
          { key: "preview", label: "Preview", icon: Eye },
          { key: "raw", label: "Raw Markdown", icon: FileText },
          { key: "api", label: "API Access", icon: Key },
        ].map((tab) => (
          <button
            key={tab.key}
            onClick={() => setActiveTab(tab.key)}
            data-testid={`kb-tab-${tab.key}`}
            className={`flex items-center gap-2 px-4 py-2 rounded-lg text-xs font-medium transition-all ${
              activeTab === tab.key
                ? "bg-[#00FF88]/10 text-[#00FF88] border border-[#00FF88]/20"
                : "text-slate-500 hover:text-white hover:bg-white/5"
            }`}
          >
            <tab.icon className="w-3.5 h-3.5" /> {tab.label}
          </button>
        ))}
      </div>

      {/* Preview Tab */}
      {activeTab === "preview" && (
        <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} className="space-y-6">
          {/* Summary */}
          {p.one_liner && (
            <div className="bg-white/[0.03] backdrop-blur-xl border border-[#00FF88]/10 rounded-2xl p-6">
              <p className="text-base text-slate-200 italic leading-relaxed">"{p.one_liner}"</p>
            </div>
          )}

          {/* Personality */}
          {(p.tags || []).length > 0 && (
            <div className="bg-white/[0.03] backdrop-blur-xl border border-white/[0.08] rounded-2xl p-6">
              <h3 className="text-sm font-semibold text-white mb-4" style={{ fontFamily: 'Manrope' }}>Personality</h3>
              <div className="flex flex-wrap gap-2 mb-4">
                {p.tags.map((t, i) => (
                  <Badge key={i} variant="outline" className="text-xs border-[#00FF88]/20 text-[#00FF88]">
                    {t.trait}
                  </Badge>
                ))}
              </div>
              <div className="grid grid-cols-2 gap-3 text-xs">
                {p.writing_tone && <div><span className="text-slate-500">Tone:</span> <span className="text-slate-300">{p.writing_tone}</span></div>}
                {p.vocabulary && <div><span className="text-slate-500">Vocabulary:</span> <span className="text-slate-300 capitalize">{p.vocabulary}</span></div>}
                {p.sentiment && <div><span className="text-slate-500">Sentiment:</span> <span className="text-slate-300 capitalize">{p.sentiment}</span></div>}
                {p.memory_style && <div><span className="text-slate-500">Memory style:</span> <span className="text-slate-300 capitalize">{p.memory_style}</span></div>}
              </div>
            </div>
          )}

          {/* Music */}
          {(s.music?.top_genres || []).length > 0 && (
            <div className="bg-white/[0.03] backdrop-blur-xl border border-white/[0.08] rounded-2xl p-6">
              <h3 className="text-sm font-semibold text-white mb-3" style={{ fontFamily: 'Manrope' }}>Music Taste</h3>
              <div className="flex flex-wrap gap-1.5 mb-3">
                {s.music.top_genres.map((g, i) => (
                  <Badge key={i} className="text-xs bg-[#1DB954]/10 text-[#1DB954] border-[#1DB954]/20">{g}</Badge>
                ))}
              </div>
              {(s.music.top_artists || []).length > 0 && (
                <p className="text-xs text-slate-400">Artists: {s.music.top_artists.join(", ")}</p>
              )}
            </div>
          )}

          {/* Values */}
          {(s.values_and_beliefs || []).length > 0 && (
            <div className="bg-white/[0.03] backdrop-blur-xl border border-white/[0.08] rounded-2xl p-6">
              <h3 className="text-sm font-semibold text-white mb-4" style={{ fontFamily: 'Manrope' }}>Values & Beliefs</h3>
              <div className="space-y-4 max-h-64 overflow-y-auto">
                {s.values_and_beliefs.slice(0, 8).map((v, i) => (
                  <div key={i}>
                    <p className="text-xs text-[#00FF88] font-medium mb-1">{v.question}</p>
                    <p className="text-xs text-slate-300 leading-relaxed">{v.answer}</p>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Relationships */}
          {(s.relationships || []).length > 0 && (
            <div className="bg-white/[0.03] backdrop-blur-xl border border-white/[0.08] rounded-2xl p-6">
              <h3 className="text-sm font-semibold text-white mb-3" style={{ fontFamily: 'Manrope' }}>Relationships</h3>
              <div className="space-y-2">
                {s.relationships.map((r, i) => (
                  <div key={i} className="flex items-center gap-2 text-xs">
                    <span className="text-white font-medium">{r.name}</span>
                    <span className="text-slate-500">— {r.relationship}</span>
                  </div>
                ))}
              </div>
            </div>
          )}
        </motion.div>
      )}

      {/* Raw Markdown Tab */}
      {activeTab === "raw" && (
        <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }}>
          <div className="relative">
            <Button
              onClick={() => handleCopy(markdown)}
              size="sm"
              className="absolute top-3 right-3 z-10 bg-white/10 text-white hover:bg-white/20 rounded-lg"
            >
              <Copy className="w-3 h-3 mr-1" /> Copy
            </Button>
            <pre className="bg-white/[0.03] backdrop-blur-xl border border-white/[0.08] rounded-2xl p-6 text-xs text-slate-300 font-mono whitespace-pre-wrap leading-relaxed max-h-[600px] overflow-y-auto">
              {markdown}
            </pre>
          </div>
        </motion.div>
      )}

      {/* API Access Tab */}
      {activeTab === "api" && apiKey && (
        <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} className="space-y-6">
          <div className="bg-white/[0.03] backdrop-blur-xl border border-[#00FF88]/10 rounded-2xl p-6">
            <div className="flex items-center gap-2 mb-4">
              <Shield className="w-4 h-4 text-[#00FF88]" />
              <h3 className="text-sm font-semibold text-white" style={{ fontFamily: 'Manrope' }}>Your API Key</h3>
            </div>
            <p className="text-xs text-slate-400 mb-4">
              Use this key to let external LLMs access your personal context. Share it only with apps you trust.
            </p>
            <div className="flex items-center gap-2 mb-4">
              <code className="flex-1 bg-white/5 border border-white/10 rounded-xl px-4 py-3 text-sm font-mono text-white">
                {showKey ? apiKey.api_key : apiKey.api_key.slice(0, 10) + "••••••••••••••••"}
              </code>
              <button onClick={() => setShowKey(!showKey)} className="p-2 text-slate-400 hover:text-white">
                {showKey ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
              </button>
              <button onClick={() => handleCopy(apiKey.api_key)} className="p-2 text-slate-400 hover:text-white">
                <Copy className="w-4 h-4" />
              </button>
            </div>
            <Button
              onClick={handleRegenKey}
              variant="outline"
              size="sm"
              className="border-white/10 text-slate-400 rounded-full"
            >
              <RefreshCw className="w-3 h-3 mr-1" /> Regenerate Key
            </Button>
          </div>

          {/* Endpoint Info */}
          <div className="bg-white/[0.03] backdrop-blur-xl border border-white/[0.08] rounded-2xl p-6">
            <h3 className="text-sm font-semibold text-white mb-4" style={{ fontFamily: 'Manrope' }}>API Endpoint</h3>
            <div className="bg-white/5 rounded-xl p-4 mb-4">
              <p className="text-xs text-slate-500 mb-1">GET</p>
              <code className="text-sm text-[#00FF88] font-mono break-all">{apiUrl}</code>
            </div>
            <p className="text-xs text-slate-400 mb-4">
              This endpoint returns your personal context as JSON — no authentication needed, just the API key in the URL.
            </p>
            <div className="bg-white/5 rounded-xl p-4">
              <p className="text-xs text-slate-500 mb-2">Usage with ChatGPT / Claude:</p>
              <p className="text-xs text-slate-300 leading-relaxed">
                Paste this in your LLM's custom instructions or system prompt:
                <br /><br />
                <code className="text-[#00FF88]">"Use the following personal context about me: [paste your exported context here]"</code>
              </p>
            </div>
          </div>
        </motion.div>
      )}
    </motion.div>
  );
}
